(async () => {
const { 
  getElement,
  store,
  useState,
  useEffect,
} = await import("@wordpress/interactivity");

// Unlike `data-wp-init` and `data-wp-watch`, you can use any hooks inside
// `data-wp-run` callbacks.
const useResize = () => {
  const [ size, setSize ] = useState( {
    width: 0,
    height: 0,
  } );

  useEffect(() => {
    const { ref } = getElement();
    const observer = new ResizeObserver(() => {
      setSize(ref.getBoundingClientRect());
    });
    observer.observe(ref);
    return () => ref && observer.unobserve(ref);
  }, []);
  
  return size;
};

const useScroll = () => {
  const documentElement = document.documentElement;
  const [ scroll, setScroll ] = useState( {
    top: window.pageYOffset || documentElement.scrollTop,
    direction: null,
  } );

  useEffect( () => {
    window.addEventListener("scroll", function() {
      let { top, direction } = scroll;

      const scrollTop = window.pageYOffset || documentElement.scrollTop;
      if (scrollTop > top && direction !== "bottom") {
        direction = "bottom";
      } else if (scrollTop < top) {
        direction = "top";
      }

      top = scrollTop <= 0 ? 0 : scrollTop; // for Mobile or negative scrolling
      setScroll({
        top,
        direction,
      });
    }, false);
  }, []);

  return scroll;
}

const formatTickerNumber = (value, withSign = false) => {
  const number = Number(value || 0);
  const sign = withSign && number > 0 ? "+" : "";
  return `${sign}${number.toFixed(2)}`;
};

const updateTickerElement = (tickerElement, payload) => {
  if (!tickerElement || !payload) {
    return;
  }

  const symbol = tickerElement.querySelector("[data-stock-symbol]");
  const last = tickerElement.querySelector("[data-stock-last]");
  const changeNumber = tickerElement.querySelector("[data-stock-change-number]");
  const changePercent = tickerElement.querySelector("[data-stock-change-percent]");
  const changeContainer = tickerElement.querySelector("[data-stock-change-container]");

  if (symbol) {
    symbol.textContent = String(payload.symbol || "").toUpperCase();
  }

  if (last) {
    last.textContent = `$${formatTickerNumber(payload.lastTrade)}`;
  }

  if (changeNumber) {
    changeNumber.textContent = formatTickerNumber(payload.changeNumber, true);
  }

  if (changePercent) {
    changePercent.textContent = `(${formatTickerNumber(payload.changePercent, true)}%)`;
  }

  if (changeContainer) {
    const change = Number(payload.changeNumber || 0);
    const isUp = change > 0;
    const isDown = change < 0;
    changeContainer.classList.toggle("is-up", isUp);
    changeContainer.classList.toggle("is-down", isDown);
    changeContainer.classList.toggle("is-neutral", !isUp && !isDown);
  }
};

const refreshStockTicker = async () => {
  const tickerElements = Array.from(document.querySelectorAll("[data-stock-ticker]"));
  if (!tickerElements.length) {
    return;
  }

  const endpoint = tickerElements[0].dataset.stockTickerEndpoint;
  if (!endpoint) {
    return;
  }

  const cacheBuster = `_=${Date.now()}`;
  const separator = endpoint.includes("?") ? "&" : "?";
  const url = `${endpoint}${separator}${cacheBuster}`;

  try {
    const response = await fetch(url, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
      },
    });

    if (!response.ok) {
      return;
    }

    const payload = await response.json();
    tickerElements.forEach((tickerElement) => updateTickerElement(tickerElement, payload));
  } catch (error) {
    return;
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", refreshStockTicker, { once: true });
} else {
  refreshStockTicker();
}

store("parts/header", {
  // Automatically reacts to state changes.
  callbacks: {
    resize() {
      const size = useResize();
      useEffect( () => {
        // Resets CSS variable for header height to the root.
        document.documentElement.style.setProperty("--spacing-header-height", `${size.height}px`);
      } );
    },
    scroll() {
      if (CSS.supports(`container-type: scroll-state`)) {
        return;
      }

      const scroll = useScroll();
      useEffect( () => {
        // Add scroll direction classes to header.
        const { ref } = getElement();
        const classList = ref.classList;
        classList.toggle("@scroll-direction-none",  !scroll.direction);
        classList.toggle("@scroll-direction-top", scroll.direction === "top");
        classList.toggle("@scroll-direction-bottom", scroll.direction === "bottom");

        const styles = window.getComputedStyle(ref);
        if (styles.position === "sticky" || styles.position === "-webkit-sticky") {
          const boundingRect = ref.getBoundingClientRect();
          classList.toggle("@scroll-stuck-top", scroll.top > boundingRect.top);
        }
      });
    }
  },
});
})();